import { useEffect, useRef, useState } from "react";

const stats = [
  { value: "100+", label: "Clients Served" },
  { value: "15", label: "Years of Experience" },
  { value: "98%", label: "Client Retention" },
  { value: "4.9★", label: "Average Rating" },
];

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = (href: string) => {
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" ref={ref} className="relative flex flex-col pt-[76px] sm:pt-[84px] md:pt-[88px] md:min-h-[100svh]">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/16e7ecc47d694bcecdfc09433f68eb11.jpg"
          alt="Professional Accounting Services"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-950/95 via-charcoal-900/85 to-charcoal-950/70" />
      </div>

      {/* Content */}
      <div className="relative flex-1 flex flex-col justify-center px-4 sm:px-6 py-6 md:py-20 w-full max-w-6xl mx-auto">
        <div
          className={`transition-all duration-1000 max-w-2xl ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-center gap-2 mb-2 md:mb-6">
            <div className="w-5 md:w-8 h-0.5 bg-forest-400" />
            <span className="text-forest-300 text-[10px] md:text-xs font-semibold tracking-[0.15em] md:tracking-[0.25em] uppercase">
              Nationwide &bull; Bookkeeping &bull; Accounting &bull; Tax
            </span>
          </div>

          <h1 className="font-cormorant text-2xl sm:text-3xl md:text-6xl lg:text-7xl font-semibold text-white leading-tight mb-2 md:mb-6">
            Professional Accounting Services
            <span className="block text-forest-300 italic">
              for Individuals &amp; Businesses
            </span>
          </h1>

          <p
            className={`text-charcoal-200 text-xs md:text-xl max-w-xl leading-relaxed mb-5 md:mb-10 font-light transition-all duration-1000 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            From bookkeeping and QuickBooks cleanup to payroll and sales tax filing
            — we handle the numbers so you can focus on growing your business.
            Remote-friendly, serving clients across the U.S.
          </p>

          <div
            className={`flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 md:gap-3 transition-all duration-1000 delay-400 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <button
              onClick={() => handleScroll("#contact")}
              className="px-5 md:px-8 py-2.5 md:py-3.5 bg-forest-600 hover:bg-forest-700 text-white rounded-md text-xs md:text-sm font-semibold transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Book a Free Consultation
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => handleScroll("#services")}
              className="px-5 md:px-8 py-2.5 md:py-3.5 bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-md text-xs md:text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center"
            >
              Explore Our Services
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div
        className={`relative bg-forest-700 transition-all duration-1000 delay-600 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-white text-base md:text-2xl font-semibold font-cormorant">{stat.value}</p>
              <p className="text-forest-200 text-[10px] md:text-xs font-medium tracking-wide mt-0.5 md:mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
